import { Box, Skeleton, Stack } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { FC } from "react";

const InfoBox = styled(Box)(({ theme }) => ({
  flex: 1,
  backgroundColor: theme.myTheme.colors.gray400,
  padding: "16px",
  borderRadius: "16px",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  height: "77px",
}));

interface TabBoxProps {
  isActive?: boolean;
}

const TabBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isActive",
})<TabBoxProps>(({ theme, isActive }) => ({
  flex: 1,
  backgroundColor: isActive ? theme.myTheme.colors.gray400 : theme.myTheme.colors.gray600,
  height: "35px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
}));

const TabSkeleton = styled(Skeleton)(({ theme }) => ({
  backgroundColor: theme.myTheme.colors.foreground20,
  margin: "0 auto",
  width: "60px",
  height: "19px",
}));

const ChartSkeleton = styled(Skeleton)(({ theme }) => ({
  backgroundColor: theme.myTheme.colors.gray500,
  borderRadius: "4px",
  height: "361px",
}));

interface TabsContainerProps {
  tabsCount?: number;
  activeTab?: number;
}

const TabsContainer = styled(Stack)({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  position: "sticky",
  top: "-20px",
  zIndex: 10,
});

const TabsSkeletonRow: FC<TabsContainerProps> = ({ tabsCount = 2, activeTab = 1 }) => {
  const tabs = Array.from({ length: tabsCount }, (_, index) => ({
    id: index + 1,
    isActive: index + 1 === activeTab,
  }));

  return (
    <TabsContainer>
      {tabs.map((tab, index) => (
        <TabBox
          key={tab.id}
          isActive={tab.isActive}
          sx={{
            ...(index === 0 && { borderTopLeftRadius: 0 }),
            ...(index === tabs.length - 1 && { borderTopRightRadius: 0 }),
          }}
        >
          <TabSkeleton variant="text" />
        </TabBox>
      ))}
    </TabsContainer>
  );
};

const SkeletonDetails: FC<TabsContainerProps> = ({ tabsCount = 2, activeTab = 1 }) => {
  const theme = useTheme();

  return (
    <Box>
      <Stack
        padding="0 8px"
        direction="row"
        gap="8px"
        justifyContent="space-between"
        marginBottom="22px"
      >
        <InfoBox>
          <Skeleton
            variant="text"
            width="40px"
            height="21px"
            sx={{ background: theme.myTheme.colors.foreground20 }}
          />
          <Skeleton
            variant="text"
            width="80px"
            height="21px"
            sx={{ background: theme.myTheme.colors.foreground10 }}
          />
        </InfoBox>
        <InfoBox>
          <Skeleton
            variant="text"
            width="40px"
            height="21px"
            sx={{ background: theme.myTheme.colors.foreground20 }}
          />
          <Skeleton
            variant="text"
            width="80px"
            height="21px"
            sx={{ background: theme.myTheme.colors.foreground10 }}
          />
        </InfoBox>
        <InfoBox>
          <Skeleton
            variant="text"
            width="40px"
            height="21px"
            sx={{ background: theme.myTheme.colors.foreground20 }}
          />
          <Skeleton
            variant="text"
            width="80px"
            height="21px"
            sx={{ background: theme.myTheme.colors.foreground10 }}
          />
        </InfoBox>
      </Stack>

      <TabsSkeletonRow tabsCount={tabsCount} activeTab={activeTab} />

      <Box sx={{ background: theme.myTheme.colors.gray400, p: "24px 16px" }}>
        <Skeleton
          variant="text"
          width="130px"
          height="21px"
          sx={{ background: theme.myTheme.colors.foreground20, mb: "12px" }}
        />

        <Stack direction="row" gap="8px" mb="12px">
          <Skeleton
            variant="text"
            width="30px"
            height="21px"
            sx={{ background: theme.myTheme.colors.foreground20 }}
          />
          <Skeleton
            variant="text"
            width="30px"
            height="21px"
            sx={{ background: theme.myTheme.colors.foreground20 }}
          />
          <Skeleton
            variant="text"
            width="80px"
            height="21px"
            sx={{ background: theme.myTheme.colors.foreground20 }}
          />
        </Stack>

        <ChartSkeleton variant="rectangular" sx={{ mb: "32px" }} />

        <Box>
          <Skeleton
            width="80px"
            height="27px"
            variant="text"
            sx={{ background: theme.myTheme.colors.foreground20, mb: "16px" }}
          />

          <Stack gap="8px">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((item) => (
              <Stack key={item} direction="row" justifyContent="space-between" alignItems="center">
                <Skeleton
                  height="21px"
                  variant="text"
                  width="80px"
                  sx={{ background: theme.myTheme.colors.foreground10 }}
                />
                <Skeleton
                  height="21px"
                  variant="text"
                  width="80px"
                  sx={{ background: theme.myTheme.colors.foreground20 }}
                />
              </Stack>
            ))}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default SkeletonDetails;
